import React from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RotateCcw, Filter } from "lucide-react";
import { useServiceLines } from "../../lib/serviceLines";

const SERVICE_LINES = ["GHA", "GHBC", "GLH", "TVA", "FH", "GHI"];
const STATUSES = ["Open", "In Progress", "Under Investigation", "Closed"];

const PRESETS = [
  { id: "7d", label: "Last 7 days", days: 7 },
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "ytd", label: "Year to date", days: null, ytd: true },
  { id: "all", label: "All time", days: null, all: true },
  { id: "custom", label: "Custom range", days: null, custom: true },
];


function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function presetToRange(preset) {
  const today = new Date();
  if (preset === "7d" || preset === "30d" || preset === "90d") {
    const days = parseInt(preset, 10);
    const start = new Date(today);
    start.setDate(start.getDate() - days);
    return { start_date: toIso(start), end_date: toIso(today) };
  }
  if (preset === "ytd") {
    return { start_date: `${today.getFullYear()}-01-01`, end_date: toIso(today) };
  }
  return { start_date: "", end_date: "" };
}

export function buildQueryString(filters) {
  const params = new URLSearchParams();
  if (filters.service_lines?.length)
    params.set("service_lines", filters.service_lines.join(","));
  if (filters.issue_types?.length)
    params.set("issue_types", filters.issue_types.join(","));
  if (filters.statuses?.length)
    params.set("statuses", filters.statuses.join(","));
  if (filters.start_date) params.set("start_date", filters.start_date);
  if (filters.end_date) params.set("end_date", filters.end_date);
  return params.toString();
}

function csvToArr(v) {
  return v ? v.split(",").filter(Boolean) : [];
}

export function useFilterState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preset = searchParams.get("preset") || "30d";
  const filters = {
    service_lines: csvToArr(searchParams.get("service_lines")),
    issue_types: csvToArr(searchParams.get("issue_types")),
    statuses: csvToArr(searchParams.get("statuses")),
    start_date: searchParams.get("start_date") || "",
    end_date: searchParams.get("end_date") || "",
    preset,
  };

  // If preset is set and date fields are missing, compute them
  React.useEffect(() => {
    if (preset !== "custom" && preset !== "all") {
      const { start_date, end_date } = presetToRange(preset);
      if (start_date && filters.start_date !== start_date) {
        const next = new URLSearchParams(searchParams);
        next.set("start_date", start_date);
        next.set("end_date", end_date);
        setSearchParams(next, { replace: true });
      }
    }
    if (preset === "all") {
      const next = new URLSearchParams(searchParams);
      let changed = false;
      if (next.has("start_date")) { next.delete("start_date"); changed = true; }
      if (next.has("end_date")) { next.delete("end_date"); changed = true; }
      if (changed) setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  const update = (patch) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (Array.isArray(v)) {
        if (v.length) next.set(k, v.join(","));
        else next.delete(k);
      } else if (v === "" || v === null || v === undefined) {
        next.delete(k);
      } else {
        next.set(k, v);
      }
    }
    setSearchParams(next, { replace: true });
  };

  const reset = () => setSearchParams(new URLSearchParams({ preset: "30d" }), { replace: true });

  return { filters, update, reset };
}

function MultiSelect({ label, options, value, onChange, testid, labelOf }) {
  const [open, setOpen] = React.useState(false);
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const renderLabel = labelOf || ((v) => v);
  const summary =
    value.length === 0
      ? `All ${label.toLowerCase()}`
      : value.length === 1
      ? renderLabel(value[0])
      : `${value.length} selected`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 rounded-xl border-[color:var(--border-subtle)] bg-[color:var(--surface)] font-normal"
          data-testid={testid}
        >
          <Filter className="mr-2 h-3.5 w-3.5 text-[color:var(--page-fg-muted)]" />
          <span className="text-[color:var(--page-fg)]">{label}:</span>
          <span className="ml-1 text-[color:var(--page-fg-muted)]">{summary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2">
        <div className="max-h-64 overflow-y-auto">
          {options.map((opt) => (
            <label
              key={opt}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-[color:var(--surface-2)]"
            >
              <Checkbox
                checked={value.includes(opt)}
                onCheckedChange={() => toggle(opt)}
                data-testid={`${testid}-option-${opt}`}
              />
              <span>{renderLabel(opt)}</span>
            </label>
          ))}
        </div>
        {value.length > 0 && (
          <button
            onClick={() => onChange([])}
            className="mt-2 w-full rounded-lg border border-[color:var(--border-subtle)] px-2 py-1 text-xs text-[color:var(--page-fg-muted)] hover:bg-[color:var(--surface-2)]"
          >
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default function FilterBar({ issueTypes, onFiltersChange }) {
  const { filters, update, reset } = useFilterState();
  const { nameOf } = useServiceLines();

  React.useEffect(() => {
    onFiltersChange?.(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.service_lines.join(","),
    filters.issue_types.join(","),
    filters.statuses.join(","),
    filters.start_date,
    filters.end_date,
  ]);

  const hasFilters =
    filters.service_lines.length ||
    filters.issue_types.length ||
    filters.statuses.length ||
    (filters.preset && filters.preset !== "30d");

  return (
    <div
      data-testid="filter-bar"
      className="sticky top-16 z-10 -mx-6 mb-6 flex flex-wrap items-center gap-2 border-b border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-6 py-3 backdrop-blur-md"
    >
      <Select
        value={filters.preset}
        onValueChange={(v) => update({ preset: v })}
      >
        <SelectTrigger className="h-9 w-44 rounded-xl border-[color:var(--border-subtle)] bg-[color:var(--surface)]" data-testid="filter-preset">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {filters.preset === "custom" && (
        <div className="flex items-center gap-1.5">
          <Input
            type="date"
            value={filters.start_date}
            onChange={(e) => update({ start_date: e.target.value })}
            className="h-9 w-40 rounded-xl"
            data-testid="filter-start-date"
          />
          <span className="text-[color:var(--page-fg-muted)]">→</span>
          <Input
            type="date"
            value={filters.end_date}
            onChange={(e) => update({ end_date: e.target.value })}
            className="h-9 w-40 rounded-xl"
            data-testid="filter-end-date"
          />
        </div>
      )}

      <MultiSelect
        label="Service line"
        options={SERVICE_LINES}
        value={filters.service_lines}
        onChange={(v) => update({ service_lines: v })}
        testid="filter-service-lines"
        labelOf={(code) => `${nameOf(code)} (${code})`}
      />
      <MultiSelect
        label="Issue type"
        options={issueTypes}
        value={filters.issue_types}
        onChange={(v) => update({ issue_types: v })}
        testid="filter-issue-types"
      />
      <MultiSelect
        label="Status"
        options={STATUSES}
        value={filters.statuses}
        onChange={(v) => update({ statuses: v })}
        testid="filter-statuses"
      />

      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={reset}
          data-testid="filter-reset"
          className="ml-auto text-[color:var(--page-fg-muted)]"
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          Reset filters
        </Button>
      )}
    </div>
  );
}
